/**
 * Announcement Bar — infinite auto-scroll
 *
 * Used by: sections/announcement-bar.liquid
 *
 * Marquee runs when ALL are true:
 *   1. "Enable infinite auto scroll" is on (adds .announcement-bar--marquee class)
 *   2. User does not prefer reduced motion
 *
 * The announcement set is cloned as many times as it takes to cover the viewport,
 * so the loop stays seamless on wide screens where one set is narrower than the bar.
 *
 * Also syncs --announcement-bar-height on <html> so the sticky header can offset correctly.
 *
 * Data attributes on the bar element:
 *   - data-announcement-bar   — init hook
 *   - data-scroll-duration    — full cycle time in seconds (default 30)
 */
(function () {
  const initAnnouncementBar = (bar) => {
    // Prevent double-init in theme editor / section reloads
    if (!bar || bar.dataset.marqueeInitialized === 'true') return;
    bar.dataset.marqueeInitialized = 'true';

    const viewport = bar.querySelector('.announcement-bar__viewport');
    const track = bar.querySelector('.announcement-bar__track');
    if (!viewport || !track) return;

    const pauseButton = bar.querySelector('[data-announcement-pause]');
    let animation = null;
    let userPaused = false;

    // Expose bar height as a CSS variable for header offset calculations
    const updateHeight = () => {
      document.documentElement.style.setProperty('--announcement-bar-height', `${bar.offsetHeight}px`);
    };

    updateHeight();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(bar);
    }

    const marqueeEnabled = bar.classList.contains('announcement-bar--marquee');
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reducedMotion = motionQuery.matches;

    let onResize = updateHeight;
    let onMouseEnter = null;
    let onMouseLeave = null;
    let onFocusIn = null;
    let onFocusOut = null;
    let onPauseClick = null;
    let measure = null;

    // The preference can change while the page is open: rebuild in the new mode
    const onMotionChange = () => {
      cleanup();
      initAnnouncementBar(bar);
    };

    // Tear down listeners, animation, and clones on section unload
    const cleanup = () => {
      animation?.cancel();
      animation = null;

      window.removeEventListener('resize', onResize);
      resizeObserver?.disconnect();
      motionQuery.removeEventListener('change', onMotionChange);

      if (onMouseEnter) viewport.removeEventListener('mouseenter', onMouseEnter);
      if (onMouseLeave) viewport.removeEventListener('mouseleave', onMouseLeave);
      if (onFocusIn) bar.removeEventListener('focusin', onFocusIn);
      if (onFocusOut) bar.removeEventListener('focusout', onFocusOut);
      if (onPauseClick) pauseButton?.removeEventListener('click', onPauseClick);
      pauseButton?.setAttribute('hidden', '');

      track.querySelectorAll('[data-announcement-clone]').forEach((clone) => clone.remove());
      delete track.dataset.cloneSets;

      delete bar.dataset.marqueeInitialized;
      delete bar._announcementCleanup;
    };

    bar._announcementCleanup = cleanup;
    motionQuery.addEventListener('change', onMotionChange);

    // Static layout only: marquee disabled in settings or user prefers reduced motion
    if (!marqueeEnabled || reducedMotion) {
      track.style.transform = '';
      window.addEventListener('resize', onResize);
      return;
    }

    const duration = (Number(bar.dataset.scrollDuration) || 30) * 1000;
    let loopWidth = 0;

    const getTrackGap = () => {
      const gapValue = parseFloat(getComputedStyle(track).gap);
      return Number.isFinite(gapValue) ? gapValue : 0;
    };

    // Original announcements only — excludes clones appended for seamless looping
    const getOriginalItems = () =>
      [...track.querySelectorAll('.announcement-bar__item:not([data-announcement-clone])')];

    // Total width of one full announcement set (used as the scroll loop distance)
    const measureOriginalWidth = () => {
      const items = getOriginalItems();
      if (items.length === 0) return 0;

      const gap = getTrackGap();
      let width = 0;

      items.forEach((item, index) => {
        width += item.offsetWidth;
        if (index > 0) width += gap;
      });

      return width;
    };

    /**
     * The scroll runs as a Web Animation on the track's transform, which the
     * browser composites off the main thread. The previous requestAnimationFrame
     * loop wrote the transform from JS every frame, so whenever scrolling or app
     * scripts kept the main thread busy it dropped frames and jumped — visible as
     * a stutter in Safari. One loop always takes `duration`, so a new width only
     * swaps the keyframes; the animation keeps its place.
     */
    const updateAnimation = () => {
      const keyframes = [
        { transform: 'translate3d(0, 0, 0)' },
        { transform: `translate3d(${-loopWidth}px, 0, 0)` },
      ];

      if (animation) {
        animation.effect.setKeyframes(keyframes);
        return;
      }

      animation = track.animate(keyframes, { duration, iterations: Infinity, easing: 'linear' });
    };

    const removeClones = () => {
      track.querySelectorAll('[data-announcement-clone]').forEach((clone) => clone.remove());
      delete track.dataset.cloneSets;
    };

    // Append `sets` copies of the announcements after the originals
    const setClones = (sets) => {
      if (Number(track.dataset.cloneSets) === sets) return;

      removeClones();
      const items = getOriginalItems();

      for (let set = 0; set < sets; set++) {
        for (const item of items) {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-announcement-clone', 'true');
          clone.setAttribute('aria-hidden', 'true');
          if (clone.tagName === 'A') clone.tabIndex = -1;
          track.appendChild(clone);
        }
      }

      track.dataset.cloneSets = sets;
    };

    /**
     * Measure one announcement set and add enough clones to cover the viewport.
     * Called on init, resize, and after images finish loading.
     */
    measure = () => {
      const originalWidth = measureOriginalWidth();
      if (originalWidth <= 0) return;

      // One loop is a full set plus the gap before the first clone
      loopWidth = originalWidth + getTrackGap();

      // The track must stay filled while it scrolls one loop to the left
      setClones(Math.max(1, Math.ceil(viewport.clientWidth / loopWidth)));

      updateAnimation();
    };

    // Without Web Animations support the bar stays static rather than falling back to a JS loop
    if (typeof track.animate !== 'function') {
      window.addEventListener('resize', onResize);
      return;
    }

    measure();

    const setUserPaused = (paused) => {
      userPaused = paused;
      if (paused) animation?.pause();
      else animation?.play();

      if (pauseButton) {
        // A toggle button: the name stays "Pause announcements" and
        // aria-pressed carries the state ("pressed" while paused). The icon
        // switches to play for sighted users.
        pauseButton.setAttribute('aria-pressed', String(paused));
        pauseButton.classList.toggle('is-paused', paused);
      }
    };

    // Pause scroll on hover and while keyboard focus is inside the bar, unless the user paused it
    onMouseEnter = () => {
      animation?.pause();
    };

    onMouseLeave = () => {
      if (!userPaused) animation?.play();
    };

    onFocusIn = () => {
      animation?.pause();
    };

    onFocusOut = () => {
      if (!userPaused && !bar.matches(':hover')) animation?.play();
    };

    onPauseClick = () => setUserPaused(!userPaused);

    onResize = () => {
      updateHeight();
      measure();
    };

    window.addEventListener('resize', onResize);
    viewport.addEventListener('mouseenter', onMouseEnter);
    viewport.addEventListener('mouseleave', onMouseLeave);
    bar.addEventListener('focusin', onFocusIn);
    bar.addEventListener('focusout', onFocusOut);

    if (pauseButton) {
      setUserPaused(false);
      pauseButton.removeAttribute('hidden');
      pauseButton.addEventListener('click', onPauseClick);
    }

    // Re-measure once lazy-loaded icons and the web fonts have their final sizes
    track.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', () => measure(), { once: true });
      }
    });
    const remeasureAfterFonts = async () => {
      await document.fonts?.ready;
      if (bar.dataset.marqueeInitialized === 'true') measure();
    };
    remeasureAfterFonts();
  };

  // Init all announcement bars on page load
  document.querySelectorAll('[data-announcement-bar]').forEach(initAnnouncementBar);

  // Theme editor: init when section is added/re-rendered
  document.addEventListener('shopify:section:load', (event) => {
    const bar = event.target.querySelector('[data-announcement-bar]');
    if (bar) initAnnouncementBar(bar);
  });

  // Theme editor: cleanup when section is removed
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelector('[data-announcement-bar]')?._announcementCleanup?.();
  });
})();